/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Box, Card, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { InternalValuationReviewTabs } from "./components/tabs/internalValuationReviewTabs";
import { SubjectPropertyInformationCard } from "./components/subjectPropertyInformationCard";
import { InternalValuationReviewStore } from "./stores/internalValuationReviewStore";
import { observer } from "mobx-react";
import { InternalValuationReport } from "./components/reports/internalValuationReport/internalValuationReport";
import { useEffect, useRef, useState } from 'react';
import { PrintableAreaRef, VerticalBarLoader } from "@roc/ui";
import { useDocumentStore } from "@roc/feature-documents";
import { Document } from '@roc/feature-types';

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    backgroundColor: "transparent",
    width: '100%',
  },
  backgroundContainer: {
    position: "absolute",
    height: "1300px",
    width: "100%",
    backgroundImage: "linear-gradient(to bottom, #ffffff, #BABEC5)",
    zIndex: -1
  },
  hiddenDiv: {
    position: "absolute",
    height: 0,
    left: "-9999px"
  },
  button: {
    minWidth: "185px",
  },
  informationDialogText: {
    margin: '-32px',
  },
}));

export enum CONFIRM_MODAL_ACTION {
  ASSIGN_TO_OWNER,
  MARK_AS_COMPLETED,
  MARK_AS_NOT_APPLICABLE,
  ESCALATE_TASK,
};

interface InternalValuationReviewProps {
  internalValuationReviewStore: InternalValuationReviewStore;
  loanId: number;
  propertyId: number;
  readOnly?: boolean;
  document?: Document;
}

export const InternalValuationReview = observer(({ loanId, propertyId, readOnly = false, internalValuationReviewStore, document }: InternalValuationReviewProps) => {
  const classes = useStyles();
  const { documentStore } = useDocumentStore();

  const internalValuationReportRef = useRef<PrintableAreaRef>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    let mounted = true;
    setIsInitializing(true);
    internalValuationReviewStore.setDefaults();
    (async () => {
      try {
        await (internalValuationReviewStore.loadInternalValuationReviewData(loanId, propertyId, readOnly, true) as unknown as Promise<void>);
      } finally {
        if (mounted) setIsInitializing(false);
      }
    })();
    return () => { mounted = false; };
  }, [loanId, propertyId, readOnly, internalValuationReviewStore]);

  const generateReportHandler = async () => {
    try {
      setIsLoading(true);
      setReportOpen(true);

      await new Promise((resolve) => setTimeout(resolve, 600));
      if (!internalValuationReportRef?.current) {
        return;
      }
      const internalValuationReportPdfBlob = await internalValuationReportRef.current.getPdfBlobUrl({
        allowTaint: true,
        useCORS: true,
      });
      const internalValuationReportResponse = await fetch(internalValuationReportPdfBlob.toString());
      const internalValuationReportBlob = await internalValuationReportResponse.blob();

      const internalValuationReportFile = new File([internalValuationReportBlob], "internalValuationReport.pdf", { type: "application/pdf", lastModified: Date.now() });

      const internalValuationReportUploadResponse: any = await internalValuationReviewStore.uploadInternalValuationReportDocument(internalValuationReportFile);

      if (internalValuationReportUploadResponse?.data?.data) {
        const loanTask = JSON.parse(internalValuationReportUploadResponse?.data?.data);
        documentStore.downloadDocument(loanTask);
      }
    } catch (err) {
      internalValuationReviewStore.globalStore.notificationStore.showErrorNotification({
        message: "There was an error generating the report.",
      });
      console.log(err);
    }
    setReportOpen(false);
    setIsLoading(false);
  };

  return (
    <Card className={classes.root} elevation={0}>
      <Box className={classes.backgroundContainer} />
      {isInitializing ? (
        <Box position="relative" width="100%" height={400} style={{ backgroundColor: 'white' }}>
          <VerticalBarLoader position="absolute" />
        </Box>
      ) : (
        <Grid container direction="column" spacing={2}>
          <Grid item style={{ paddingInline: "1.5rem" }} xs={12}>
            <SubjectPropertyInformationCard internalValuationReviewStore={internalValuationReviewStore} generateReport={generateReportHandler} document={document} />
          </Grid>
          <Grid item xs={12}>
            <InternalValuationReviewTabs internalValuationReviewStore={internalValuationReviewStore} />
          </Grid>
        </Grid>
      )}
      {reportOpen && (<div className={classes.hiddenDiv}>
        <InternalValuationReport internalValuationReviewStore={internalValuationReviewStore} ref={internalValuationReportRef} />
      </div>)}
      {isLoading && <VerticalBarLoader />}
    </Card>
  );
});